import create from 'zustand';
import { Project, ProjectCreate, ProjectUpdate, ProjectWithCount } from '../types/project';
import { projectApi } from '../api/projectApi';

interface ProjectState {
  projects: Project[];
  currentProject: ProjectWithCount | null;
  isLoading: boolean;
  error: string | null;
  
  // Pagination
  page: number;
  rowsPerPage: number;
  
  // Sorting
  orderBy: 'code' | 'name' | 'created_on' | 'status' | 'issues_count';
  order: 'asc' | 'desc';
  
  // Filtering
  search: string;
  status: 'all' | 'active' | 'inactive';
  year: string;
  
  // Actions
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  setOrderBy: (orderBy: 'code' | 'name' | 'created_on' | 'status' | 'issues_count') => void;
  setOrder: (order: 'asc' | 'desc') => void;
  setSearch: (search: string) => void;
  setStatus: (status: 'all' | 'active' | 'inactive') => void;
  setYear: (year: string) => void;
  
  fetchProjects: () => Promise<void>;
  fetchProject: (id: number) => Promise<void>;
  createProject: (data: ProjectCreate) => Promise<void>;
  updateProject: (id: number, data: ProjectUpdate) => Promise<void>;
  deleteProject: (id: number) => Promise<void>;
  clearCurrentProject: () => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  isLoading: false,
  error: null,
  
  // Pagination
  page: 0,
  rowsPerPage: 10,
  
  // Sorting
  orderBy: 'code',
  order: 'asc',
  
  // Filtering
  search: '',
  status: 'all',
  year: 'all',
  
  // Actions
  setPage: (page) => set({ page }),
  setRowsPerPage: (rowsPerPage) => set({ rowsPerPage, page: 0 }),
  setOrderBy: (orderBy) => set({ orderBy }),
  setOrder: (order) => set({ order }),
  setSearch: (search) => set({ search, page: 0 }),
  setStatus: (status) => set({ status, page: 0 }),
  setYear: (year) => set({ year, page: 0 }),

  fetchProjects: async () => {
    const { page, rowsPerPage, orderBy, order, search, status, year } = get();
    set({ isLoading: true, error: null });
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        order_by: orderBy,
        order,
        search: search || undefined,
        status: status === 'all' ? undefined : status === 'active',
        year: year === 'all' ? undefined : parseInt(year),
      };
      const projects = await projectApi.getProjects(params);
      set({ projects, error: null });
    } catch (error: any) {
      set({ 
        error: error.response?.data?.detail || 'Failed to fetch projects',
        projects: [] 
      });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const project = await projectApi.getProject(id);
      set({ currentProject: project });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to fetch event' });
    } finally {
      set({ isLoading: false });
    }
  },

  createProject: async (data) => {
    set({ isLoading: true, error: null });
    try {
      await projectApi.createProject(data);
      get().fetchProjects();
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to create event' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateProject: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const updatedProject = await projectApi.updateProject(id, data);
      set(state => ({
        projects: state.projects.map(p => p.id === id ? updatedProject : p),
        currentProject: state.currentProject?.id === id ? { ...state.currentProject, ...updatedProject } : state.currentProject
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to update event' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await projectApi.deleteProject(id);
      set(state => ({
        projects: state.projects.filter(p => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to delete event' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  clearCurrentProject: () => {
    set({ currentProject: null });
  },
}));
